import { useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Collapse,
  TextField,
  Typography,
  CircularProgress,
  Link,
} from '@mui/material';
import LockResetIcon from '@mui/icons-material/LockReset';
import { isValidEmail } from '../../Helpers/utils';
import { forgotPassword as apiForgotPassword } from '../../Helpers/dataApi';
import PageLayout from '../../Components/PageLayout/PageLayout';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [emailIsValid, setEmailIsValid] = useState(true);
  const [feedback, setFeedback] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const trimmedEmail = email.trim();
    const isValid = isValidEmail(trimmedEmail);
    setEmailIsValid(isValid);

    if (isValid) {
      setLoading(true);
      setFeedback(null);
      try {
        await apiForgotPassword(trimmedEmail);
        setFeedback({
          severity: 'success',
          message: 'Check your email for password reset instructions',
        });
        setEmail('');
      } catch (error) {
        // The backend intentionally returns 200 even for unknown emails (security best practice),
        // so a thrown error here means a genuine server/network problem.
        setFeedback({
          severity: 'error',
          message: error?.response?.data?.error || 'Server error, please try again later',
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleChange = (event) => {
    const { value } = event.target;
    setEmail(value);
    if (value) {
      setEmailIsValid(isValidEmail(value));
    }
  };

  return (
    <PageLayout maxWidth="xs">
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          flexDirection: 'column',
          marginTop: 8,
        }}
      >
        <Avatar sx={{ m: 1, bgcolor: 'primary.main' }}>
          <LockResetIcon />
        </Avatar>
        <Typography component="h1" variant="h5">
          Reset Password
        </Typography>
        <Box component="form" noValidate onSubmit={handleSubmit} sx={{ mt: 1 }}>
          <TextField
            autoComplete="email"
            autoFocus
            error={!emailIsValid}
            fullWidth
            helperText={!emailIsValid ? 'Please enter a valid email address' : ''}
            margin="normal"
            id="email"
            label="Email Address"
            name="email"
            value={email}
            onChange={handleChange}
            required
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Send Email'}
          </Button>
          {feedback && (
            <Collapse in={!!feedback} sx={{ width: '100%' }}>
              <Alert severity={feedback.severity} sx={{ mt: '0.5rem', textAlign: 'center' }}>
                {feedback.message}
              </Alert>
            </Collapse>
          )}
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Link
              component="button"
              variant="body2"
              onClick={() => window.location.href = '/login'}
              sx={{ cursor: 'pointer' }}
            >
              Back to Login
            </Link>
          </Box>
        </Box>
      </Box>
    </PageLayout>
  );
}
